import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/apiClient";
import { devLog } from "@/lib/devLog";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=guest, {}=user
  const [ready, setReady] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch (error) {
        // Not authenticated — expected for guests
        devLog.debug("Auth check: no active session", error?.response?.status);
        setUser(false);
      } finally {
        setReady(true);
      }
    })();
  }, []);

  /**
   * Login. If the account has 2FA enabled, the server returns 401 with
   * `detail.code === "otp_required"`. Caller should catch that, prompt for
   * the OTP, and call login again passing `otp`.
   */
  const login = useCallback(async (email, password, otp) => {
    // Session cookie is set httpOnly by the server — nothing to persist client-side.
    const body = { email, password };
    if (otp) body.otp = otp;
    const { data } = await api.post("/auth/login", body);
    setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      devLog.debug("Logout call failed (proceeding with client cleanup):", error?.response?.status);
    }
    setUser(false);
  }, []);

  const ctxValue = useMemo(
    () => ({ user, ready, login, logout, setUser }),
    [user, ready, login, logout],
  );

  return (
    <AuthCtx.Provider value={ctxValue}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
